/**
 * View Complaint Dialog
 * Read-only dialog for viewing complaint details
 */

import React, { useState, useEffect } from 'react';
import { BaseDialog } from './index';
import FilePreviewDialog from './FilePreviewDialog';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import config from '../../config/environment';
import complaintService from '../../services/complaintService';

// Helper function to construct full file URL from relative path
const getFileUrl = (filePath) => {
  if (!filePath) return '';

  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get base URL without /api suffix
  const baseURL = config.api.baseURL.replace('/api', '');

  // Construct full URL - Laravel typically serves files from /storage/
  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

  // Construct URL: baseURL/storage/filePath
  return `${baseURL}/storage/${cleanPath}`;
};

const ViewComplaintDialog = ({ isOpen, onClose, complaint }) => {
  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: ''
  });
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (isOpen && complaint?.complaint_id) {
      fetchHistory(complaint.complaint_id);
    }
  }, [isOpen, complaint]);

  const fetchHistory = async (id) => {
    setLoadingHistory(true);
    try {
      const response = await complaintService.getUserComplaintHistoryLogs(id);
      if (response.success && response.data?.history) {
        setHistoryData(transformHistoryToStatusUpdates(response.data.history));
      } else {
        setHistoryData([]);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const transformHistoryToStatusUpdates = (history) => {
    if (!Array.isArray(history)) return [];

    return history.map((item) => {
      const date = item.updated_at
        ? new Date(item.updated_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
        : 'N/A';

      let status = item.status || item.new_value || 'N/A';
      if (item.action_type === 'status_change' && item.new_value) {
        status = item.new_value;
      }

      let isRichContent = false;
      if (typeof status === 'string' && status.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(status);
          const changes = [];

          Object.keys(parsed).forEach(key => {
            if (parsed[key] !== null && parsed[key] !== undefined && parsed[key] !== '') {
              const formattedKey = key
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              changes.push(`${formattedKey}: ${parsed[key]}`);
            }
          });

          if (changes.length > 0) {
            status = changes.join(' | ');
            isRichContent = true;
          }
        } catch (e) {
          // ignore
        }
      }

      const updatedBy = item.action_by?.role_name || 'System';
      const comment = item.remark || 'No remarks provided';

      return {
        date,
        comment,
        status,
        isRichContent,
        updatedBy
      };
    });
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-neutral-100 text-neutral-800';

    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('under review') || statusLower.includes('investigation')) {
      return 'bg-primary-100 text-primary-800';
    } else if (statusLower.includes('pending')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusLower.includes('approved') || statusLower.includes('resolved')) {
      return 'bg-secondary-100 text-secondary-800';
    } else if (statusLower.includes('rejected')) {
      return 'bg-red-100 text-red-800';
    } else if (statusLower.includes('closed')) {
      return 'bg-neutral-100 text-neutral-800';
    }
    return 'bg-neutral-100 text-neutral-800';
  };

  if (!complaint) return null;

  const handlePreviewFile = (url, name) => {
    setPreviewDialog({
      isOpen: true,
      fileUrl: url,
      fileName: name,
      fileType: '' // Type unknown from URL usually, but FilePreviewDialog handles name extension
    });
  };

  const renderFooter = () => (
    <div className="flex justify-end">
      <button
        onClick={onClose}
        className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Complaint ID: ${complaint.complaint_id}`}
      size="large"
      footer={renderFooter()}
    >
      <div className="space-y-6">
        {/* Complainant Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Complainant Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="font-medium text-gray-900">{complaint.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mobile Number</p>
              <p className="font-medium text-gray-900">{complaint.mobile}</p>
            </div>
            {complaint.email && (
              <div>
                <p className="text-sm text-gray-500">Email Address</p>
                <p className="font-medium text-gray-900">{obfuscateEmail(complaint.email)}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium text-gray-900">{complaint.date}</p>
            </div>
            {complaint.address && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium text-gray-900">{complaint.address}</p>
              </div>
            )}
          </div>
        </div>

        {/* Complaint Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Complaint Details</h3>
          <div className="bg-white p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{complaint.complaint}</p>
          </div>
        </div>

        {/* Documents */}
        {complaint.documents && complaint.documents.length > 0 && (() => {
          // Segregate documents
          const supportingDocs = complaint.documents.filter(doc =>
            doc.file_type === 'supporting' ||
            (typeof doc !== 'string' && !doc.file_type) // Default to supporting if no type
          );
          const generalDocs = complaint.documents.filter(doc => doc.file_type === 'general');

          const renderFileList = (files, title) => {
            if (!files || files.length === 0) return null;
            return (
              <div className="bg-gray-50 p-4 rounded-lg mb-4 last:mb-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
                <div className="space-y-2">
                  {files.map((file, index) => {
                    const filePath = typeof file === 'string' ? file : (file.file_path || file.path || file.name);
                    const getFileNameFromPath = (path) => {
                      if (!path) return 'Document';
                      return path.split('/').pop() || 'Document';
                    };
                    const fileName = typeof file === 'string' ? file : (file.name || file.original_name || getFileNameFromPath(file.file_type ? `${file.file_type}-${getFileNameFromPath(filePath)}` : filePath));
                    const fileUrl = getFileUrl(filePath);

                    return (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg">
                        <div className="flex items-center min-w-0">
                          <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm text-gray-900 truncate">{fileName}</span>
                        </div>
                        <button
                          onClick={() => handlePreviewFile(fileUrl, fileName)}
                          className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                          aria-label="View document"
                          title="View Document"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          };

          return (
            <>
              {renderFileList(supportingDocs, 'Supporting Documents')}
              {renderFileList(generalDocs, 'General Documents')}
            </>
          );
        })()}

        {/* Status History */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Updates</h3>
          {loadingHistory ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading history...</p>
            </div>
          ) : historyData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status/Update</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historyData.map((update, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{update.date}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{update.comment}</td>
                      <td className="px-4 py-3 text-sm">
                        {update.isRichContent ? (
                          <span className="text-gray-700 block whitespace-pre-wrap">
                            {update.status}
                          </span>
                        ) : (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(update.status)}`}>
                            {update.status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{update.updatedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No status updates available.</p>
          )}
        </div>
      </div>
      <FilePreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={() => setPreviewDialog({ ...previewDialog, isOpen: false })}
        fileUrl={previewDialog.fileUrl}
        fileName={previewDialog.fileName}
        fileType={previewDialog.fileType}
      />
    </BaseDialog>
  );
};

export default ViewComplaintDialog;

